/**
 * ErrorBoundary.tsx — React class-based error boundary components.
 *
 * Two variants:
 *   1. `PageErrorBoundary`  — full-page friendly error (used per route/page)
 *   2. `GlobalErrorBoundary` — catches any uncaught render error at the app root
 *
 * Usage in App.tsx:
 *   <GlobalErrorBoundary>
 *     <Suspense fallback={<LoadingScreen />}>
 *       <Routes>...</Routes>
 *     </Suspense>
 *   </GlobalErrorBoundary>
 *
 * Usage per page:
 *   <PageErrorBoundary>
 *     <InventoryPage />
 *   </PageErrorBoundary>
 *
 * Or use the `withErrorBoundary` HOC:
 *   const SafeInventory = withErrorBoundary(InventoryPage);
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// State / Props
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. Receives error + reset function. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called when an error is caught — useful for logging to Sentry etc. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

// ---------------------------------------------------------------------------
// Base class
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class ErrorBoundaryBase extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ errorInfo: info });
    this.props.onError?.(error, info);

    // Development: log full stack trace
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error, info);
    }
  }

  reset(): void {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  override render(): ReactNode {
    const { hasError, error } = this.state;

    if (!hasError || !error) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return null;
  }
}

// ---------------------------------------------------------------------------
// Page-level error boundary
// ---------------------------------------------------------------------------

export class PageErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ errorInfo: info });
    this.props.onError?.(error, info);
    if (import.meta.env.DEV) {
      console.error('[PageErrorBoundary]', error, info);
    }
  }

  reset(): void {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  override render(): ReactNode {
    const { hasError, error } = this.state;

    if (!hasError || !error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, () => this.reset());

    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 p-6">
        <div className="text-4xl select-none">⚠️</div>
        <div className="text-center">
          <p className="text-gray-800 font-semibold text-lg">Ocurrió un error en esta sección</p>
          <p className="text-gray-500 text-sm mt-1 max-w-sm">
            {import.meta.env.DEV ? error.message : 'Hubo un problema al cargar este contenido.'}
          </p>
        </div>
        <button
          onClick={() => this.reset()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
        >
          Reintentar
        </button>
        {import.meta.env.DEV && (
          <details className="mt-2 text-xs text-gray-400 max-w-xl w-full">
            <summary className="cursor-pointer">Stack trace (DEV)</summary>
            <pre className="mt-2 overflow-auto p-3 bg-gray-50 rounded text-red-600 text-xs whitespace-pre-wrap">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

// ---------------------------------------------------------------------------
// Global error boundary (app root)
// ---------------------------------------------------------------------------

export class GlobalErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ errorInfo: info });
    this.props.onError?.(error, info);
    console.error('[GlobalErrorBoundary] Unhandled render error:', error, info);
  }

  override render(): ReactNode {
    const { hasError, error } = this.state;

    if (!hasError || !error) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8 bg-gray-50">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4 select-none">🔴</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">La aplicación encontró un error</h1>
          <p className="text-gray-500 text-sm mb-6">
            {import.meta.env.DEV
              ? error.message
              : 'Algo salió mal. Por favor recarga la página o contacta a soporte.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
            >
              Recargar página
            </button>
            <button
              onClick={() => { window.location.href = '/dashboard'; }}
              className="px-4 py-2 border text-gray-600 text-sm rounded hover:bg-gray-50"
            >
              Ir al inicio
            </button>
          </div>
          {import.meta.env.DEV && (
            <details className="mt-4 text-left">
              <summary className="text-xs text-gray-400 cursor-pointer">Stack trace (DEV)</summary>
              <pre className="mt-2 overflow-auto p-3 bg-gray-50 rounded text-red-600 text-xs whitespace-pre-wrap text-left">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

// ---------------------------------------------------------------------------
// HOC wrapper
// ---------------------------------------------------------------------------

/**
 * Wraps a page component with a PageErrorBoundary.
 *
 * Usage:
 *   const SafeInventoryPage = withErrorBoundary(InventoryPage);
 *   // Then use <SafeInventoryPage /> instead of <InventoryPage />
 */
export function withErrorBoundary<P extends object>(
  Wrapped: React.ComponentType<P>,
  onError?: (error: Error, info: ErrorInfo) => void,
): React.ComponentType<P> {
  return function WithErrorBoundaryWrapper(props: P) {
    return (
      <PageErrorBoundary onError={onError}>
        <Wrapped {...props} />
      </PageErrorBoundary>
    );
  };
}
